/**
 * Microsoft Authentication Library (MSAL) Configuration
 * 
 * This file configures MSAL for SharePoint/OneDrive integration.
 * Uses Authorization Code flow with PKCE for secure browser-based authentication.
 */

import { Configuration, LogLevel, PublicClientApplication } from "@azure/msal-browser";

// =============================================================================
// MSAL Configuration
// =============================================================================

export const msalConfig: Configuration = {
  auth: {
    // Your Azure AD app registration Client ID
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || "",
    
    // Multi-tenant authority - allows any Microsoft account
    // "organizations" = work/school accounts only (SharePoint users)
    // "consumers" = personal Microsoft accounts only (OneDrive Personal)
    // "common" = both work/school AND personal accounts
    authority: "https://login.microsoftonline.com/common",
    
    // Where Microsoft redirects after authentication
    redirectUri: import.meta.env.VITE_MICROSOFT_REDIRECT_URI || "http://localhost:8080/auth/microsoft/callback",
    
    // Where to redirect after logout
    postLogoutRedirectUri: typeof window !== "undefined" ? window.location.origin : "",
    
    // Navigate back to the page that initiated login
    navigateToLoginRequestUrl: true,
  },
  
  cache: {
    // localStorage persists across browser sessions so users stay connected
    cacheLocation: "localStorage",
    
    // Set to true for IE11 or Edge legacy support
    storeAuthStateInCookie: false,
  },
  
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        // Never log Personally Identifiable Information
        if (containsPii) return;
        
        switch (level) {
          case LogLevel.Error:
            console.error("[MSAL]", message);
            break;
          case LogLevel.Warning:
            console.warn("[MSAL]", message);
            break;
          case LogLevel.Info:
            // Uncomment for debugging:
            // console.info("[MSAL]", message);
            break;
          case LogLevel.Verbose:
            // Uncomment for detailed debugging:
            // console.debug("[MSAL]", message);
            break;
        }
      },
      // Set to Verbose for debugging, Warning for production
      logLevel: LogLevel.Warning,
    },
    // `allowNativeBroker` retiré dans msal-browser v4 — broker natif désactivé par défaut
  },
};

// =============================================================================
// Permission Scopes
// =============================================================================

/**
 * Scopes define what permissions the app requests from Microsoft.
 * 
 * Personal accounts (live.fr, outlook.com, hotmail.com):
 *   - Only have access to OneDrive Personal
 *   - Cannot use Sites.Read.All (SharePoint-only)
 * 
 * Business/School accounts (Microsoft 365):
 *   - Have access to OneDrive for Business AND SharePoint
 *   - Can use Sites.Read.All
 */
export const sharePointScopes = {
  // Minimal scope - just authenticate and get user info
  login: ["User.Read"],
  
  // OneDrive scope - works for BOTH personal and business accounts
  // This is the default scope we request during login
  onedrive: [
    "User.Read",           // Read user profile
    "Files.Read.All",      // Read all files in OneDrive
  ],
  
  // SharePoint scope - ONLY works for business/school accounts
  // We request this dynamically after detecting account type
  sharepoint: [
    "Sites.Read.All",      // Read SharePoint site information
  ],
};

// =============================================================================
// MSAL Instance
// =============================================================================

/**
 * Singleton MSAL PublicClientApplication instance.
 * This is used throughout the app for authentication operations.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// =============================================================================
// Initialization
// =============================================================================

let isInitialized = false;
let initializationPromise: Promise<void> | null = null;

/**
 * Initialize MSAL and handle any redirect response.
 * Safe to call multiple times - only initializes once.
 */
export const initializeMsal = async (): Promise<void> => {
  // If already initialized, return immediately
  if (isInitialized) {
    return;
  }
  
  // If initialization is in progress, wait for it
  if (initializationPromise) {
    return initializationPromise;
  }
  
  // Start initialization
  initializationPromise = (async () => {
    try {
      // Initialize the MSAL instance
      await msalInstance.initialize();
      
      // Handle redirect response (if coming back from Microsoft login)
      const response = await msalInstance.handleRedirectPromise();
      
      if (response) {
        console.log("✅ Microsoft authentication successful:", response.account?.username);
      }
      
      isInitialized = true;
    } catch (error) {
      console.error("❌ MSAL initialization error:", error);
      initializationPromise = null; // Allow retry on error
      throw error;
    }
  })();
  
  return initializationPromise;
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if MSAL is properly configured
 */
export const isMsalConfigured = (): boolean => {
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
  return Boolean(clientId && clientId.length > 0);
};

/**
 * Get all currently signed-in Microsoft accounts
 */
export const getMicrosoftAccounts = () => {
  return msalInstance.getAllAccounts();
};

/**
 * Check if user has an active Microsoft session
 */
export const hasMicrosoftSession = (): boolean => {
  return msalInstance.getAllAccounts().length > 0;
};

